import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiRequest, queryClient } from '@/lib/queryClient';
import { ChevronLeft, Eye, Users, Search, ClipboardList, Bot } from "lucide-react";
import { HugeiconsIcon } from '@hugeicons/react';
import { AddSquareIcon, FilterVerticalIcon, Settings01Icon } from '@hugeicons/core-free-icons';
import { useLocation } from "wouter";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  isTelegramWebApp,
  getTelegramWebApp,
  showNewViewsNotification,
  showBackButton as showTelegramBackButton,
  hideBackButton as hideTelegramBackButton
} from "@/lib/telegram-service";
import { useApp } from "@/lib/context";
import { useIsDesktop } from '@/hooks/use-device-context';
import { translations } from "@/lib/translations";
// ParticipationNotifications removed - no longer part of notification center
import { getProfileViewers, type ProfileView } from '@/lib/profile-views-api';
import { TelegramImage } from '@/components/profile/telegram-image';
import { SimpleCitySelector } from '@/components/simple-city-selector';
import { GuestGuard } from '@/components/guest';
import {
  FaUsers,
  FaVenus,
  FaMars,
  FaExchangeAlt,
  FaTransgender
} from 'react-icons/fa';
import { GiHandcuffs, GiWhip } from 'react-icons/gi';

// Componente custom per l'icona Couple usando coppia.svg (colorato quando selezionato) e coppia bianca.svg (bianco quando non selezionato)
const CoupleIcon = ({ className, isSelected }: { className?: string, isSelected?: boolean }) => (
  <img
    src={isSelected ? "/assets/images/coppia.svg" : "/assets/images/coppia bianca.svg"}
    alt="Couple"
    className={className}
    style={{ width: '16px', height: '16px' }}
  />
);
import { useAuth } from '@/hooks/use-auth';
import { getToken } from '@/lib/token-storage';
import { VipBadge } from '@/components/vip-badge';
import { useToast } from '@/hooks/use-toast';
import { useAppVersion } from '@/hooks/use-app-version';
import { UserSearchSheet } from '@/components/user-search-sheet';
import { FilterSheet } from '@/components/filter-sheet';
import { ProfileType, mapOldCategoryToProfileType } from '@shared/schema';
import SpeedDateCreationSheet from '@/components/speed-dates/speed-date-creation-sheet';


interface HeaderProps {
  customTitle?: string;  // Titolo personalizzato opzionale
  showLogo?: boolean;    // Mostra il logo (default: true)
  showBackButton?: boolean; // Mostra il pulsante indietro (default: false)
  onBackClick?: () => void; // Funzione da chiamare quando viene cliccato il pulsante indietro
  isProfileOpen?: boolean; // Indica se il profilo utente è aperto
  isEventSheetOpen?: boolean; // Indica se la scheda evento è aperta
  hideSecondRow?: boolean; // Nasconde la seconda riga (avatar + city selector)
}

// Componente per la sezione versione
function VersionSection({ setExpandedVersion, appVersion }: { setExpandedVersion: (value: boolean) => void; appVersion: string }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const { data: versionData } = useQuery({
    queryKey: ['/api/app-version'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/app-version');
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    },
    staleTime: 300000, // 5 minuti
  });

  const changelogs = versionData?.changelogs || [];
  const currentVersion = versionData?.version || appVersion;

  // Dati del carousel delle funzionalità
  const slides = [
    {
      icon: '✨',
      title: 'Free for Everyone',
      description: 'No subscription, no surprises. All features available to all.',
      image: '/assets/images/CAACAgIAAxkBAWLiVWmCqwABTs74GJMY2_0ac5tEEVt9HQACAwEAAladvQoC5dF4.gif'
    },
    {
      icon: '🛡️',
      title: 'Total Privacy',
      description: 'No email, no phone number saved. Secure login via Telegram.',
      image: '/assets/images/CAACAgEAAxkBAWLiRWmCqdOK4A7n8GU3qWA2_5zAWq9UAAINAAPfTigZ_a68Vfs1.gif'
    },
    {
      icon: '👁️',
      title: 'Controlled Visibility',
      description: 'You will be seen ONLY by categories you select. Others will not see you.',
      image: '/assets/images/CAACAgIAAxkBAWAMC2kyfpNyAQ4dI0RSuEekJrVZKxdOAAL3AANWnb0KC3IkHUj0.gif'
    },
    {
      icon: '🎉',
      title: 'All in One App',
      description: 'Events, Speed Dating, Travel, Kinky Tests, BDSM Contracts',
      image: '/assets/images/CAACAgEAAxkBAWLiammCq_VU7PW4v3SbJmdTaO9KGthjAAIkAAPfTigZQjiRoGQZ.gif'
    }
  ];

  // Auto-scroll carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  // Touch handlers per swipe
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    } else if (isRightSwipe) {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  return (
    <div className="w-full pb-20">
      <div className="flex items-center gap-2 mb-4 px-4">
        <img
          src="/assets/images/brand/icon-64.webp"
          alt="Kinkygram"
          className="w-6 h-6 rounded-full"
        />
        <h3 className="text-lg font-semibold text-white">
          Kinkygram <span className="text-blue-400">{currentVersion}</span> <span className="text-yellow-400 text-xs">BETA</span>
        </h3>
      </div>
      
      {/* Carousel Section */}
      <div className="px-4 mb-6">
        <div 
          className="relative overflow-hidden"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {/* Slides */}
          <div 
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {slides.map((slide, index) => (
              <div 
                key={index}
                className="w-full flex-shrink-0 flex flex-col items-center px-4 text-center"
              >
                {/* Titolo con icona */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">{slide.icon}</span>
                  <h3 className="text-base font-bold text-white">
                    {slide.title}
                  </h3>
                </div>

                {/* Descrizione */}
                <p className="text-xs text-white/70 leading-relaxed mb-3 max-w-[280px]">
                  {slide.description}
                </p>

                {/* Immagine animata */}
                <div className="w-32 h-32">
                  <img 
                    src={slide.image}
                    alt={slide.title}
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots Indicator */}
        <div className="flex items-center justify-center gap-2 mt-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                currentSlide === index 
                  ? "bg-blue-600 w-5" 
                  : "bg-white/30 w-1.5"
              }`}
            />
          ))}
        </div>
      </div>
      
      {/* Changelog Section */}
      <div className="px-2 pb-6">
        <h4 className="text-sm font-medium text-white/80 mb-3">Version History</h4>
        <div className="space-y-3">
          {changelogs.length > 0 ? (
            changelogs.slice(0, 5).map((log: any, index: number) => (
              <div key={index} className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">{log.version}</p>
                <p className="text-sm text-white/80">{log.changelog}</p>
                <p className="text-xs text-white/40 mt-1">
                  {new Date(log.releasedAt).toLocaleDateString()}
                </p>
              </div>
            ))
          ) : (
            <>
              {/* Version 0.9 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.9 - Social Evolution</p>
                <p className="text-xs text-white/60 mb-1">Feb 5, 2026</p>
                <p className="text-sm text-white/80">Telegram CDN, video uploads in wall posts, BDSM Contracts 2.0, new role system, travel posts</p>
              </div>
              
              {/* Version 0.8 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.8 - Admin & Payments</p>
                <p className="text-xs text-white/60 mb-1">Dec 15, 2025</p>
                <p className="text-sm text-white/80">Bank transfers, Admin Dashboard 2.0, Kinky Test, BDSM Contracts, Ban system</p>
              </div>
              
              {/* Version 0.7 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.7 - Crypto & Web3</p>
                <p className="text-xs text-white/60 mb-1">Oct 28, 2025</p>
                <p className="text-sm text-white/80">TON Integration, TON Connect, crypto payments, TON wallet setup</p>
              </div>
              
              {/* Version 0.6 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.6 - Events & Monetization</p>
                <p className="text-xs text-white/60 mb-1">Sep 10, 2025</p>
                <p className="text-sm text-white/80">Stripe Integration, event coupons, extra services, event participants management</p>
              </div>
              
              {/* Version 0.5 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.5 - Speed & Travel</p>
                <p className="text-xs text-white/60 mb-1">Jul 22, 2025</p>
                <p className="text-sm text-white/80">Speed dating, travel planning, speed stories, travel quarter container</p>
              </div>
              
              {/* Version 0.4 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.4 - Groups & Telegram</p>
                <p className="text-xs text-white/60 mb-1">Jun 5, 2025</p>
                <p className="text-sm text-white/80">Telegram group integration, group approval workflow, group poster management</p>
              </div>
              
              {/* Version 0.3 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.3 - Events & Discovery</p>
                <p className="text-xs text-white/60 mb-1">Apr 28, 2025</p>
                <p className="text-sm text-white/80">Event system, event categories, event posters, discover page</p>
              </div>
              
              {/* Version 0.2 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.2 - Wall & Media</p>
                <p className="text-xs text-white/60 mb-1">Apr 5, 2025</p>
                <p className="text-sm text-white/80">The Wall, photo gallery, mentions system, image viewer</p>
              </div>
              
              {/* Version 0.1 */}
              <div className="bg-white/5 rounded-lg p-3 text-left">
                <p className="text-xs text-blue-400 mb-1">V 0.1 - MVP Launch</p>
                <p className="text-xs text-white/60 mb-1">Mar 15, 2025</p>
                <p className="text-sm text-white/80">Telegram authentication, user registration, real-time chat, partner linking</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Componente unificato per notifiche (views, mentions, upvotes, comments)
function UnifiedNotificationsSection({ setExpandedEmoji }: { setExpandedEmoji: (value: number | null) => void }) {
  const { user } = useAuth();
  const validLanguages = ['en', 'it', 'es', 'de', 'pt', 'fr'];
  const userLanguage = (validLanguages.includes(user?.language || '') ? user?.language : 'en') as keyof typeof translations;
  const t = translations[userLanguage] || translations.en;

  // Query per le views
  const { data: viewsData, isLoading: loadingViews } = useQuery({
    queryKey: ['/api/profile-views/recent'],
    queryFn: async () => {
      console.log('[UnifiedNotifications] 🔍 Fetching views...');
      try {
        const views = await getProfileViewers();
        console.log('[UnifiedNotifications] ✅ Views received:', views);
        return views || [];
      } catch (error) {
        console.error('[UnifiedNotifications] ❌ Error fetching views:', error);
        return [];
      }
    },
    enabled: !!user?.id,
    staleTime: 30000,
    gcTime: 60000,
    retry: 1,
  });

  // Query per i commenti sui post del wall
  const { data: commentsData, isLoading: loadingComments } = useQuery({
    queryKey: ['/api/wall/comments/received'],
    queryFn: async () => {
      console.log('[UnifiedNotifications] 🔍 Fetching wall comments...');
      try {
        const response = await fetch('/api/wall/comments/received', {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch comments');
        const data = await response.json();
        console.log('[UnifiedNotifications] ✅ Comments received:', data);
        return data;
      } catch (error) {
        console.error('[UnifiedNotifications] ❌ Error fetching comments:', error);
        return { comments: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 30000,
    gcTime: 60000,
    retry: 1,
  });

  // Query per le validazioni
  const { data: validationsData, isLoading: loadingValidations } = useQuery({
    queryKey: ['/api/validations/recent'],
    queryFn: async () => {
      console.log('[UnifiedNotifications] 🔍 Fetching validations...');
      try {
        const result = await apiRequest('GET', '/api/validations/recent');
        console.log('[UnifiedNotifications] ✅ Validations received:', result);
        return result;
      } catch (error) {
        console.error('[UnifiedNotifications] ❌ Error fetching validations:', error);
        return { validations: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 30000,
    gcTime: 60000,
    retry: 1,
  });

  // Query per le mentions (wall posts)
  const { data: mentionsData, isLoading: loadingMentions } = useQuery({
    queryKey: ['/api/wall/mentions'],
    queryFn: async () => {
      console.log('[UnifiedNotifications] 🔍 Fetching mentions...');
      try {
        const response = await fetch('/api/wall/mentions', {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch mentions');
        const data = await response.json();
        console.log('[UnifiedNotifications] ✅ Mentions received:', data);
        return data;
      } catch (error) {
        console.error('[UnifiedNotifications] ❌ Error fetching mentions:', error);
        return { mentions: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 30000,
    gcTime: 60000,
    retry: 1,
  });

  // Query per gli upvotes (wall posts)
  const { data: upvotesData, isLoading: loadingUpvotes } = useQuery({
    queryKey: ['/api/wall/upvotes/received'],
    queryFn: async () => {
      console.log('[UnifiedNotifications] 🔍 Fetching upvotes...');
      try {
        const response = await fetch('/api/wall/upvotes/received', {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        if (!response.ok) throw new Error('Failed to fetch upvotes');
        const data = await response.json();
        console.log('[UnifiedNotifications] ✅ Upvotes received:', data);
        return data;
      } catch (error) {
        console.error('[UnifiedNotifications] ❌ Error fetching upvotes:', error);
        return { upvotes: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 30000,
    gcTime: 60000,
    retry: 1,
  });

  const views = viewsData || [];
  const mentions = mentionsData?.mentions || [];
  const upvotes = upvotesData?.upvotes || [];
  const comments = commentsData?.comments || [];

  // Unifica e ordina per data (più recenti prima)
  // NOTA: validations e reactions rimosse dal centro notifiche
  const unifiedNotifications = React.useMemo(() => {
    const combined: any[] = [
      ...views.map((v: any) => ({ ...v, notificationType: 'view', date: new Date(v.viewedAt) })),
      ...mentions.map((m: any) => ({ ...m, notificationType: 'mention', date: new Date(m.mentionedAt || m.postCreatedAt) })),
      ...upvotes.map((u: any) => ({ ...u, notificationType: 'upvote', date: new Date(u.votedAt) })),
      ...comments.map((c: any) => ({ ...c, notificationType: 'comment', date: new Date(c.createdAt) }))
    ];

    return combined.sort((a, b) => b.date.getTime() - a.date.getTime());
  }, [views, mentions, upvotes, comments]);

  const handleUserClick = (userId: number) => {
    const event = new CustomEvent('openUserProfile', {
      detail: { userId }
    });
    document.dispatchEvent(event);
    setExpandedEmoji(null);
  };

  if ((loadingViews || loadingMentions || loadingUpvotes || loadingComments) && unifiedNotifications.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (unifiedNotifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <img
          src="/assets/emoji/megaphone.webp"
          alt="📣"
          className="w-16 h-16 mx-auto cursor-pointer transition-all duration-300 hover:scale-110"
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            filter: 'drop-shadow(0 0 2px rgba(37, 99, 235, 0.3))'
          }}
        />
        <h3 className="font-medium text-[#f5f5f5] text-lg mt-4">
          No activity yet
        </h3>
        <p className="text-sm text-[#708499] mt-1">
          {t.notifications?.feedNotificationsWillAppear || "Your feed notifications will appear here"}
        </p>
      </div>
    );
  }

  const getActionText = (notification: any) => {
    switch (notification.notificationType) {
      case 'view':
        return t.notifications?.visitedYou || 'visited you';
      case 'mention':
        return t.notifications?.mentionedYou || 'mentioned you';
      case 'upvote':
        return t.notifications?.upvotedYourPost || 'upvoted your post';
      case 'comment':
        return t.notifications?.commentedOnYourPost || 'commented on your post:';
      default:
        return '';
    }
  };

  const getUserAge = (notification: any) => {
    let age = null;
    let partnerAge = null;
    if (notification.notificationType === 'view') {
      age = notification.viewer?.age || notification.viewerAge;
      partnerAge = notification.viewer?.partnerAge || notification.viewerPartnerAge;
    } else if (notification.notificationType === 'mention') {
      return null; // mentions non hanno età
    } else if (notification.notificationType === 'upvote') {
      age = notification.voter?.age;
      partnerAge = notification.voter?.partnerAge;
    } else if (notification.notificationType === 'comment') {
      age = notification.commenter?.age;
      partnerAge = notification.commenter?.partnerAge;
    }
    if (age && partnerAge) {
      return `${age}/${partnerAge}`;
    }
    return age;
  };

  const getUserId = (notification: any) => {
    if (notification.notificationType === 'view') {
      return notification.viewer?.id || notification.viewerId;
    } else if (notification.notificationType === 'mention') {
      return notification.authorId;
    } else if (notification.notificationType === 'upvote') {
      return notification.voter?.id || notification.voterId;
    } else if (notification.notificationType === 'comment') {
      return notification.commenter?.id;
    }
    return null;
  };

  const getUsername = (notification: any) => {
    if (notification.notificationType === 'view') {
      return notification.viewer?.username || notification.viewerUsername;
    } else if (notification.notificationType === 'mention') {
      return notification.authorUsername;
    } else if (notification.notificationType === 'upvote') {
      return notification.voter?.username;
    } else if (notification.notificationType === 'comment') {
      return notification.commenter?.username;
    }
    return 'Unknown';
  };

  const getPhotoUrl = (notification: any) => {
    if (notification.notificationType === 'view') {
      // viewer può avere photos array o profilePhotoUrl
      if (notification.viewer?.photos && notification.viewer.photos.length > 0) {
        return notification.viewer.photos[0]?.url || notification.viewer.photos[0];
      }
      return notification.viewer?.profilePhotoUrl || notification.viewerPhoto;
    } else if (notification.notificationType === 'mention') {
      return notification.authorPhoto;
    } else if (notification.notificationType === 'upvote') {
      return notification.voter?.profilePhotoUrl;
    } else if (notification.notificationType === 'comment') {
      return notification.commenter?.profilePhotoUrl;
    }
    return null;
  };

  const getUserCategory = (notification: any) => {
    if (notification.notificationType === 'view') {
      return notification.viewer?.category || notification.viewerCategory;
    } else if (notification.notificationType === 'mention') {
      return null; // mentions non hanno categoria
    } else if (notification.notificationType === 'upvote') {
      return notification.voter?.category;
    } else if (notification.notificationType === 'comment') {
      return notification.commenter?.category;
    }
    return null;
  };

  const getCategoryColor = (category: string | null) => {
    if (!category) return 'text-white/80';
    switch (category) {
      case 'Couple':
        return 'text-white/80'; // non viola
      case 'Lady':
        return 'text-pink-400';
      case 'Gentleman':
        return 'text-blue-500';
      case 'Dom (M)':
      case 'Sub (M)':
      case 'Switch (M)':
      case 'Gay':
      case 'Bisexual (M)':
      case 'Lover (M)':
        return 'text-blue-500';
      case 'Dom (F)':
      case 'Sub (F)':
      case 'Switch (F)':
      case 'Lesbian':
      case 'Bisexual (F)':
      case 'Lover (F)':
        return 'text-pink-400';
      case 'Transgender':
        return 'text-purple-400';
      default:
        return 'text-white/80';
    }
  };

  // Formatta il timestamp relativo (sempre numerico e minimale)
  const formatRelativeTime = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
      return 'now';
    } else if (diffMin < 60) {
      return `${diffMin}m`;
    } else if (diffHour < 24) {
      return `${diffHour}h`;
    } else if (diffDay < 7) {
      return `${diffDay}d`;
    } else {
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' });
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="flex items-center gap-2 mb-4 px-2">
        <img
          src="/assets/emoji/megaphone.webp"
          alt="📣"
          className="w-5 h-5"
          style={{
            userSelect: 'none',
            WebkitUserSelect: 'none'
          }}
        />
        <h3 className="text-lg font-semibold text-white">Feeds</h3>
      </div>

      {unifiedNotifications.map((notification, index) => {
        const userId = getUserId(notification);
        const username = getUsername(notification);
        const age = getUserAge(notification);
        const photoUrl = getPhotoUrl(notification);
        const actionText = getActionText(notification);
        const category = getUserCategory(notification);
        const ageColorClass = getCategoryColor(category);

        return (
        <div key={`${notification.notificationType}-${index}-${notification.date.getTime()}`} className="feed-item-v2">
          <div
            className="flex items-center gap-3 py-1.5 px-2 cursor-pointer min-h-[48px]"
            onClick={() => userId && handleUserClick(userId)}
          >
            {/* Avatar */}
            <div className="flex-shrink-0 self-center align-middle h-8 flex items-center justify-center">
              <TelegramImage
                username={username}
                userId={userId}
                alt={username}
                photoUrl={photoUrl}
                className="w-8 h-8 rounded-full"
                variant="avatar"
              />
            </div>

            {/* Username - età - azione */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-x-2 gap-y-0 flex-nowrap leading-tight overflow-hidden">
                <VipBadge
                  userId={userId}
                  username={username}
                  size="md"
                  className="font-medium truncate text-sm max-w-[100px]"
                />
                {age && (
                  (() => {
                    // Se l'età contiene una slash (coppia), splitta e mostra con colori separati
                    if (typeof age === 'string' && age.includes('/')) {
                      const [hisAge, herAge] = age.split('/');
                      return (
                        <div className="flex items-center gap-0">
                          <span className="text-blue-500 text-sm font-medium">{hisAge}</span>
                          <span className="text-white/60 text-sm">/</span>
                          <span className="text-pink-400 text-sm font-medium">{herAge}</span>
                        </div>
                      );
                    }
                    // Altrimenti, singola età con colore basato sulla categoria
                    return (
                      <span className={`${ageColorClass} flex-shrink-0 text-sm`}>{age}</span>
                    );
                  })()
                )}
                <span className="text-white/60 flex-shrink-0 text-sm">{actionText}</span>
                {/* Timestamp relativo */}
                <span className="text-white/40 flex-shrink-0 text-xs ml-auto">
                  {formatRelativeTime(notification.date)}
                </span>
              </div>
              {/* Show comment text for comment notifications - inline and compact */}
              {notification.notificationType === 'comment' && notification.content && (
                <div className="text-[11px] text-white truncate max-w-full leading-none text-left">
                  {notification.content}
                </div>
              )}
            </div>
          </div>

          {/* Separatore */}
          {index < unifiedNotifications.length - 1 && (
            <div className="border-b border-gray-700/30 mx-2"></div>
          )}
        </div>
        );
      })}

      {unifiedNotifications.length >= 20 && (
        <div className="text-center pt-2">
          <span className="text-xs text-gray-500">
            {t.notifications?.showingLatestActivities || "Showing the latest 20 activities"}
          </span>
        </div>
      )}
    </div>
  );
}

// Componente per la sezione validazioni
function ValidationNotificationsSection() {
  const { user } = useAuth();

  // Query per ottenere le validazioni recenti (accettate)
  const { data: validationsData, isLoading, error } = useQuery({
    queryKey: ['/api/validations/recent'],
    queryFn: async () => {
      console.log('[ValidationNotifications] 🔍 Fetching validations...');
      try {
        const result = await apiRequest('GET', '/api/validations/recent');
        console.log('[ValidationNotifications] ✅ Validations received:', result);
        return result;
      } catch (error) {
        console.error('[ValidationNotifications] ❌ Error fetching validations:', error);
        return { validations: [] };
      }
    },
    enabled: !!user?.id,
    staleTime: 30000,
    gcTime: 60000,
    retry: 1,
  });

  // Debug log
  React.useEffect(() => {
    console.log('[ValidationNotifications] 📊 Query state:', {
      userId: user?.id,
      isLoading,
      hasError: !!error,
      validationsCount: validationsData?.validations?.length || 0
    });
  }, [user?.id, isLoading, error, validationsData]);

  const validations = validationsData?.validations || [];

  // Mappa emoji per tipo validazione
  const validationEmojis: Record<string, string> = {
    'met': 'https://em-content.zobj.net/source/apple/419/handshake_1f91d.png',
    'played': 'https://em-content.zobj.net/source/apple/419/party-popper_1f389.png',
    'relationship': 'https://em-content.zobj.net/source/apple/419/red-heart_2764-fe0f.png'
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <p style={{ color: 'var(--tg-hint-color, #708499)' }}>
          Errore nel caricare le validazioni
        </p>
      </div>
    );
  }

  if (validations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <h3 className="font-medium text-[#f5f5f5] text-lg">
          No validations yet
        </h3>
        <p className="text-sm text-[#708499] mt-1">
          Your validation notifications will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-lg font-semibold text-white">Validations</h3>
      </div>

      {validations.map((validation: any, index: number) => {
        return (
          <div key={index} className="mb-4">
            <p>Validation {index}</p>
          </div>
        );
      })}
    </div>
  );
}

export default function Header({
  customTitle,
  showLogo = true,
  showBackButton = false,
  onBackClick,
  isProfileOpen = false,
  isEventSheetOpen = false,
  hideSecondRow = false
}: HeaderProps) {
  const isDesktop = useIsDesktop();
  const [location, setLocation] = useLocation();
  // App version - dynamically loaded from database
  const { version: APP_VERSION } = useAppVersion();

  const [expandedEmoji, setExpandedEmoji] = useState<number | null>(null);
  const [expandedVersion, setExpandedVersion] = useState<boolean>(false);
  const [newViewsCount, setNewViewsCount] = useState<number>(0);
  const [newCommentsCount, setNewCommentsCount] = useState<number>(0);
  const [hasSeenNewVersion, setHasSeenNewVersion] = useState<boolean>(false);

  // Query per controllare l'ultima versione dell'app
  const { data: appVersionData } = useQuery({
    queryKey: ['/api/app-version'],
    queryFn: async () => {
      try {
        const response = await fetch('/api/app-version');
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        return null;
      }
    },
    staleTime: 300000, // 5 minuti
  });

  // Verifica se c'è una nuova versione
  const latestVersion = appVersionData?.version || APP_VERSION;
  const hasNewVersion = appVersionData?.version && appVersionData.version !== APP_VERSION;

  // Effect to check if user has already seen the latest version
  useEffect(() => {
    const storedSeenVersion = localStorage.getItem('lastSeenVersion');
    if (hasNewVersion) {
      // If there's a new version and user hasn't seen it yet
      if (storedSeenVersion !== latestVersion) {
        setHasSeenNewVersion(false);
      } else {
        // User has already seen this version
        setHasSeenNewVersion(true);
      }
    } else {
      // No new version, check if user has seen current version
      setHasSeenNewVersion(storedSeenVersion === APP_VERSION);
    }
  }, [hasNewVersion, latestVersion, APP_VERSION]);

  // Unified notification count (views + comments) - feeds and reactions removed per requirements
  const totalNotificationsCount = newViewsCount + newCommentsCount;
  const { isAnyProfileSheetOpen, isEventSheetOpen: contextEventSheetOpen, eventEditMode, eventEditModeToggle } = useApp();
  const { user } = useAuth();
  
  // State per tracciare se stiamo visualizzando il proprio profilo
  const [isViewingOwnProfile, setIsViewingOwnProfile] = useState(false);

  // Listener per sapere se stiamo visualizzando il proprio profilo
  useEffect(() => {
    const handleOwnProfileStatus = (event: CustomEvent) => {
      console.log('[Header] 📥 Received ownProfileStatusChanged event:', event.detail);
      setIsViewingOwnProfile(event.detail.isOwnProfile);
      console.log('[Header] 🔄 Updated isViewingOwnProfile to:', event.detail.isOwnProfile);
    };

    document.addEventListener('ownProfileStatusChanged', handleOwnProfileStatus as EventListener);
    console.log('[Header] 👂 Listening for ownProfileStatusChanged events');

    return () => {
      document.removeEventListener('ownProfileStatusChanged', handleOwnProfileStatus as EventListener);
    };
  }, []);
  const { toast } = useToast();

  const [showSearchSheet, setShowSearchSheet] = useState(false);
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [showSpeedCreationSheet, setShowSpeedCreationSheet] = useState(false);

  // State for category filters - synced with localStorage
  const [selectedFilters, setSelectedFilters] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('categoryFilters');
      if (stored) {
        return JSON.parse(stored);
      }
      // Default to user's visibility_preferences or all profile types
      return [ProfileType.COUPLE, ProfileType.FEMALE, ProfileType.MALE, ProfileType.TRANS, ProfileType.TRAV];
    } catch {
      return [ProfileType.COUPLE, ProfileType.FEMALE, ProfileType.MALE, ProfileType.TRANS, ProfileType.TRAV];
    }
  });

  // Handle filter changes and sync with localStorage + dispatch event
  const handleFiltersChange = useCallback((newFilters: string[]) => {
    setSelectedFilters(newFilters);
    localStorage.setItem('categoryFilters', JSON.stringify(newFilters));
    // Dispatch event for discover page to listen
    const event = new CustomEvent('filtersChanged', { detail: { filters: newFilters } });
    document.dispatchEvent(event);
  }, []);

  // Hydrate filters from the DB (source of truth) so chips stay in sync across devices.
  // Runs once per session when visibility_preferences first arrives; localStorage is just
  // the immediate fallback for the pre-hydration window.
  const hasHydratedFiltersFromDB = useRef(false);
  useEffect(() => {
    if (hasHydratedFiltersFromDB.current) return;
    if (!user?.id) return;

    const raw = (user as any).visibility_preferences;
    let parsed: string[] | null = null;
    try {
      if (Array.isArray(raw)) parsed = raw;
      else if (typeof raw === 'string' && raw.trim() !== '') parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }

    if (Array.isArray(parsed) && parsed.length > 0) {
      setSelectedFilters(parsed);
      localStorage.setItem('categoryFilters', JSON.stringify(parsed));
      hasHydratedFiltersFromDB.current = true;
    } else if (parsed !== null) {
      // Server returned an empty array — DB says "no preferences"; mark hydrated so we
      // don't loop, but don't clobber the localStorage default with []
      hasHydratedFiltersFromDB.current = true;
    }
  }, [user?.id, (user as any)?.visibility_preferences]);


  // Use context event sheet state if available, otherwise use prop
  const finalIsEventSheetOpen = contextEventSheetOpen || isEventSheetOpen;

  // 👁️ Timestamp dell'ultimo check per evitare re-render - persistente in localStorage
  const [lastViewsCheckTimestamp, setLastViewsCheckTimestamp] = useState(() => {
    const stored = localStorage.getItem('lastViewsCheckTimestamp');
    return stored ? parseInt(stored) : (Date.now() - (10 * 60 * 1000));
  });

  const [lastCommentsCheckTimestamp, setLastCommentsCheckTimestamp] = useState(() => {
    const stored = localStorage.getItem('lastCommentsCheckTimestamp');
    return stored ? parseInt(stored) : (Date.now() - (10 * 60 * 1000));
  });

  // 🔧 Funzione per aggiornare il timestamp quando le notifiche vengono visualizzate
  const markNotificationsAsViewed = useCallback(() => {
    console.log('[Header] 📍 Aggiornamento timestamp - notifiche visualizzate');
    const newTimestamp = Date.now();
    setLastViewsCheckTimestamp(newTimestamp);
    localStorage.setItem('lastViewsCheckTimestamp', newTimestamp.toString());

    // 🔄 Invalida la query per forzare il ricalcolo del badge
    // queryClient.invalidateQueries({ queryKey: ['/api/users/profile-viewers', 'badge-counter'] });
  }, []);

  // 🔧 Funzione per aggiornare il timestamp dei commenti
  const markCommentsAsViewed = useCallback(() => {
    console.log('[Header] 📍 Aggiornamento timestamp - commenti visualizzati');
    const newTimestamp = Date.now();
    setLastCommentsCheckTimestamp(newTimestamp);
    localStorage.setItem('lastCommentsCheckTimestamp', newTimestamp.toString());
  }, []);

  const handleBackClick = () => {
    // Feedback tattile per navigazione back
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
        console.log('[Header] ⬅️ Feedback tattile - back button');
      } else if (navigator.vibrate) {
        navigator.vibrate(40);
        console.log('[Header] 📳 Fallback vibrazione - back button');
      }
    } catch (error) {
      console.error('[Header] ❌ Errore feedback tattile back button:', error);
    }

    if (onBackClick) {
      onBackClick();
    }
  };

  const handleEmojiClick = (index: number) => {
    // Feedback tattile per apertura/chiusura centro notifiche
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        if (expandedEmoji === index) {
          // Chiusura - feedback leggero
          window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
          console.log('[Header] 🔔 Feedback tattile - chiusura centro notifiche');
        } else {
          // Apertura - feedback medio
          window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
          console.log('[Header] 🔔 Feedback tattile - apertura centro notifiche');
        }
      } else if (navigator.vibrate) {
        // Fallback browser vibration
        if (expandedEmoji === index) {
          navigator.vibrate(30); // Vibrazione più leggera per chiusura
        } else {
          navigator.vibrate(50); // Vibrazione più forte per apertura
        }
        console.log('[Header] 📳 Fallback vibrazione browser - centro notifiche');
      }
    } catch (error) {
      console.error('[Header] ❌ Errore feedback tattile centro notifiche:', error);
    }

    setExpandedEmoji(expandedEmoji === index ? null : index);
  };

  // Funzione per gestire l'apertura della scheda views (ora non fa nulla)
  const handleViewsOpened = useCallback(() => {
    // Non aggiorniamo più il timestamp qui - lo faremo quando si chiude
  }, []);

  // 🔥 WebSocket listener per notifiche istantanee - UNICA FONTE DI AGGIORNAMENTI
  React.useEffect(() => {
    if (!user?.id) {
      console.log('[Header] ⚠️ No user ID, skipping WebSocket setup');
      return;
    }

    let cleanupFn: (() => void) | null = null;

    import('@/lib/socket-service').then(({ socketService }) => {
      console.log('[Header] 🔌 Inizializzazione WebSocket listener per user:', user.id);
      console.log('[Header] 📊 Socket status:', {
        isConnected: socketService.isConnected(),
        isStable: socketService.isStable(),
        socketId: socketService.getSocketId(),
        currentRoom: socketService.getCurrentRoom()
      });

      // Connetti e passa userId per join room
      if (!socketService.isConnected()) {
        console.log('[Header] 🔌 WebSocket non connesso, connessione in corso...');
        socketService.connect(user.id);
      } else {
        console.log('[Header] ✅ WebSocket già connesso');
        socketService.connect(user.id); // Assicura che sia nella room corretta
      }

      // Listener per nuove visualizzazioni profilo
      const handleProfileView = (data: any) => {
        console.log('[Header] 🔔🔔🔔 NUOVA VISUALIZZAZIONE RICEVUTA VIA WEBSOCKET! 🔔🔔🔔');
        console.log('[Header] 📦 Dati ricevuti:', data);
        console.log('[Header] 👤 Viewer:', data.viewerUsername, 'ID:', data.viewerId);
        console.log('[Header] 🕐 Timestamp:', new Date(data.viewedAt).toISOString());
        console.log('[Header] 🔍 Socket status when received:', {
          isConnected: socketService.isConnected(),
          socketId: socketService.getSocketId(),
          currentRoom: socketService.getCurrentRoom()
        });

        // 🔥 AGGIORNAMENTO IMMEDIATO: Verifica timestamp prima di incrementare
        const newViewTimestamp = new Date(data.viewedAt).getTime();
        const shouldIncrement = newViewTimestamp > lastViewsCheckTimestamp;

        console.log('[Header] 🔴 WebSocket event - verifica timestamp:', {
          newViewTimestamp,
          lastCheck: lastViewsCheckTimestamp,
          shouldIncrement
        });

        if (shouldIncrement) {
          setNewViewsCount(prev => {
            const newCount = prev + 1;
            console.log('[Header] 🔴 Badge count:', prev, '->', newCount);
            return newCount;
          });
          console.log('[Header] 🔔 Badge incrementato via WebSocket');
        } else {
          console.log('[Header] ⏭️ View già vista, badge non incrementato');
        }
      };

      // Listener per nuovi commenti sui post del wall
      const handleNewWallComment = (data: any) => {
        console.log('[Header] 💬 NUOVO COMMENTO WALL VIA WEBSOCKET!', data);
        const commentTimestamp = new Date(data.createdAt || data.timestamp).getTime();
        const shouldIncrement = commentTimestamp > lastCommentsCheckTimestamp;

        if (shouldIncrement) {
          setNewCommentsCount(prev => prev + 1);
          console.log('[Header] 💬 Badge comments incrementato');
        }
      };

      // 🔧 TELEGRAM MINI APP FIX: Registra listener IMMEDIATAMENTE (Socket.IO lo attiverà quando connesso)
      console.log('[Header] 🚀 Registrazione IMMEDIATA listener profile-view');
      console.log('[Header] 📊 Current socket state:', {
        isConnected: socketService.isConnected(),
        isStable: socketService.isStable(),
        socketId: socketService.getSocketId()
      });

      // ✅ CRITICAL FIX: Registra SUBITO tutti i listener - Socket.IO li salva e li attiva quando connesso
      // NOTA: new-participation e new-photo-reaction rimossi - non più supportati
      socketService.on('profile-view', handleProfileView);
      socketService.on('new-wall-comment', handleNewWallComment);
      console.log('[Header] ✅ Listener registrati IMMEDIATAMENTE per eventi: profile-view, new-wall-comment');
      console.log('[Header] 📝 Se socket non ancora connesso, listener saranno attivi appena connette');

      // Cleanup
      cleanupFn = () => {
        console.log('[Header] 🧹 Cleanup WebSocket listeners');
        socketService.off('profile-view', handleProfileView);
        socketService.off('new-wall-comment', handleNewWallComment);
      };
    });

    // Return cleanup function
    return () => {
      if (cleanupFn) {
        cleanupFn();
      }
    };
  }, [user?.id, lastViewsCheckTimestamp, lastCommentsCheckTimestamp]); // Added timestamps to dependencies

  // React Query - SOLO per calcolo iniziale, ZERO polling
  // 🔧 Controllo sicurezza: verifica che QueryClient sia disponibile (previene errori HMR Replit)
  const queryClient = useQueryClient();
  const isQueryClientReady = !!queryClient;

  const { data: profileViewsData, isLoading: isLoadingViews, error: viewsError } = useQuery({
    queryKey: ['/api/users/profile-viewers', 'badge-counter'],
    enabled: !!user?.id && isQueryClientReady, // Abilita solo se QueryClient è pronto
    queryFn: async () => {
      console.log('[Header] 🔄 React Query - calcolando visualizzazioni totali...');

      const viewers = await getProfileViewers();

      console.log('[Header] 🔍 TUTTE le visualizzazioni dal server:', {
        totalCount: viewers.length,
        allViewers: viewers.map(v => ({
          username: v.viewer?.username,
          viewedAt: v.viewedAt,
          viewTimestamp: new Date(v.viewedAt).getTime()
        }))
      });

      // Conta TUTTE le visualizzazioni uniche (non filtriamo per timestamp qui)
      const uniqueViewers = viewers.reduce((acc: ProfileView[], current: ProfileView) => {
        const existingIndex = acc.findIndex(v => v.viewerUserId === current.viewerUserId);
        if (existingIndex === -1) {
          acc.push(current);
        } else if (new Date(current.viewedAt) > new Date(acc[existingIndex].viewedAt)) {
          acc[existingIndex] = current;
        }
        return acc;
      }, [] as ProfileView[]);

      const totalViewsCount = uniqueViewers.length;
      console.log('[Header] 🔴 React Query Badge - visualizzazioni totali:', totalViewsCount);

      return {
        totalViewsCount,
        viewers,
        uniqueViewers,
        lastCalculated: Date.now()
      };
    },
    refetchInterval: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    staleTime: 30000, // Cache per 30 secondi
    gcTime: 60000,
    retry: 1,
  });

  // Query per contare commenti iniziali sui post del wall
  const { data: commentsData } = useQuery({
    queryKey: ['/api/wall/comments/received', 'badge-counter'],
    enabled: !!user?.id && isQueryClientReady,
    queryFn: async () => {
      console.log('[Header] 🔄 React Query - calcolando commenti iniziali...');
      const token = getToken();
      const response = await fetch('/api/wall/comments/received', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Errore nel recuperare i commenti');
      }

      const result = await response.json();
      const comments = result?.comments || [];

      // Filtra per timestamp
      const newComments = comments.filter((c: any) => {
        const commentTimestamp = new Date(c.createdAt).getTime();
        return commentTimestamp > lastCommentsCheckTimestamp;
      });

      console.log('[Header] 💬 Commenti iniziali:', newComments.length);
      return { count: newComments.length, comments };
    },
    refetchInterval: false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    staleTime: 30000,
    gcTime: 60000,
    retry: 1,
  });

  // 📊 Aggiorna lo stato del badge quando i dati React Query cambiano
  React.useEffect(() => {
    if (profileViewsData?.totalViewsCount !== undefined) {
      // Filtra le visualizzazioni totali in base all'ultimo timestamp di controllo
      // Questo assicura che il badge mostri solo le visualizzazioni *nuove* dall'ultima volta che la scheda eyes è stata chiusa.
      const filteredCount = profileViewsData.viewers.filter(view => new Date(view.viewedAt).getTime() > lastViewsCheckTimestamp).length;

      console.log('[Header] 🔴 React Query Badge - Aggiornamento stato:', {
        totalCount: profileViewsData.totalViewsCount,
        filteredCount,
        lastCheck: new Date(lastViewsCheckTimestamp).toISOString()
      });

      // Usa filteredCount invece di totalViewsCount per aggiornare newViewsCount
      if (filteredCount !== newViewsCount) {
        setNewViewsCount(filteredCount);
      }
    }
  }, [profileViewsData?.viewers, lastViewsCheckTimestamp, newViewsCount]); // Aggiunto lastViewsCheckTimestamp e viewers alle dipendenze

  // 📊 Aggiorna badge commenti quando i dati cambiano
  React.useEffect(() => {
    if (commentsData?.count !== undefined && commentsData.count !== newCommentsCount) {
      console.log('[Header] 💬 Aggiornamento badge comments:', commentsData.count);
      setNewCommentsCount(commentsData.count);
    }
  }, [commentsData?.count, newCommentsCount]);

  // 🐛 Log degli errori React Query
  React.useEffect(() => {
    if (viewsError) {
      console.error('[Header] ❌ React Query Views Error:', viewsError);
    }
  }, [viewsError]);

  // 👁️ Ref per tracciare lo stato precedente di expandedEmoji
  const prevExpandedEmojiRef = React.useRef<number | null>(null);

  // 👁️ Effect per resettare i badge quando apri il feed unificato
  React.useEffect(() => {
    // APERTURA TAB - Reset all badges immediato
    if (expandedEmoji === 0 && prevExpandedEmojiRef.current !== 0) {
      console.log('[Header] 📣 Feed unificato APERTO - reset tutti i badge');
      setNewCommentsCount(0);
      setNewViewsCount(0);
    }

    // CHIUSURA TAB - Aggiorna timestamp
    if (prevExpandedEmojiRef.current === 0 && expandedEmoji !== 0) {
      console.log('[Header] 📍 Feed unificato CHIUSO - aggiornamento tutti i timestamp');
      const newTimestamp = Date.now();

      // Update all timestamps when unified feed is closed
      setLastCommentsCheckTimestamp(newTimestamp);
      localStorage.setItem('lastCommentsCheckTimestamp', newTimestamp.toString());

      setLastViewsCheckTimestamp(newTimestamp);
      localStorage.setItem('lastViewsCheckTimestamp', newTimestamp.toString());
    }

    // Aggiorna il riferimento per il prossimo render
    prevExpandedEmojiRef.current = expandedEmoji;
  }, [expandedEmoji, queryClient]);

  // 🔄 Effect per sincronizzare il timestamp da localStorage quando cambia la pagina/location
  useEffect(() => {
    const storedTimestamp = localStorage.getItem('lastViewsCheckTimestamp');
    if (storedTimestamp) {
      const parsedTimestamp = parseInt(storedTimestamp);
      if (parsedTimestamp !== lastViewsCheckTimestamp) {
        console.log('[Header] 🔄 Sincronizzazione timestamp da localStorage:', new Date(parsedTimestamp).toISOString());
        setLastViewsCheckTimestamp(parsedTimestamp);
      }
    }
  }, [location, lastViewsCheckTimestamp]); // Trigger quando cambia la location

  // Back button per il centro notifiche
  useEffect(() => {
    if (expandedEmoji !== null || expandedVersion) {
      console.log('[Header] Showing BackButton for notifications panel');
      const backButtonId = showTelegramBackButton(() => {
        console.log('[Header] BackButton pressed, closing notifications panel');
        setExpandedEmoji(null);
        setExpandedVersion(false);
      });

      return () => {
        if (backButtonId) {
          console.log('[Header] Hiding BackButton for notifications panel');
          hideTelegramBackButton(backButtonId);
        }
      };
    }
  }, [expandedEmoji, expandedVersion]);

  // Listener per chiudere il centro notifiche quando si apre il wall modal
  useEffect(() => {
    const handleCloseNotificationsPanel = () => {
      console.log('[Header] 🔔 Chiusura centro notifiche da evento closeNotificationsPanel');
      setExpandedEmoji(null);
      setExpandedVersion(false);
    };

    document.addEventListener('closeNotificationsPanel', handleCloseNotificationsPanel);
    return () => {
      document.removeEventListener('closeNotificationsPanel', handleCloseNotificationsPanel);
    };
  }, []);

  // Single unified feed notification
  const feedNotification = {
    staticEmoji: "📣",
    staticUrl: "/assets/images/staticemo/megaphone_1f4e3.png",
    animatedUrl: "/assets/emoji/megaphone.webp",
    alt: "📣"
  };

  // Query per le foto dell'utente
  const { data: userPhotos } = useQuery({
    queryKey: ['/api/users/photos'],
    queryFn: async () => {
      const response = await fetch('/api/users/photos', {
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch user photos');
      return response.json();
    },
    enabled: !!user?.id,
    staleTime: 30000,
    gcTime: 60000,
  });

  // Query per ottenere le validazioni pending per il badge di notifica
  const { data: validationsData } = useQuery({
    queryKey: [`/api/users/${user?.id}/validations`],
    queryFn: async () => {
      if (!user?.id) return { pending: [] };
      return await apiRequest('GET', `/api/users/${user.id}/validations`);
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const pendingValidationsCount = validationsData?.pending?.length || 0;

  // Query per ottenere le richieste di iscrizione pending per il badge admin
  const { data: pendingApplicationsData } = useQuery({
    queryKey: ['/api/pending-users/admin/applications'],
    queryFn: async () => {
      if (!user?.isAdmin) return [];
      return await apiRequest('GET', '/api/pending-users/admin/applications');
    },
    enabled: !!user?.isAdmin,
    staleTime: 30 * 1000,
    gcTime: 2 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  const pendingApplicationsCount = pendingApplicationsData?.applications?.length || pendingApplicationsData?.count || 0;

  // Query per verificare se l'utente è organizzatore/co-organizzatore
  const { data: organizerStatusData } = useQuery({
    queryKey: [`/api/users/${user?.id}/group-owner-status`],
    queryFn: async () => {
      return await apiRequest('GET', `/api/users/${user?.id}/group-owner-status`);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
  const isOrganizer = organizerStatusData?.isGroupOwner === true;

  // Dropdown menu state
  const [showAddDropdown, setShowAddDropdown] = useState(false);

  const addMenuItems = [
    { id: 'post', label: 'Post', icon: '📝', action: 'openCreatePost' },
    { id: 'speed', label: 'Speed', icon: '⚡', action: 'openSpeed' },
    { id: 'travel', label: 'Travel', icon: '/assets/images/staticemo/airplane_2708-fe0f.png', action: 'openTravel' },
    { id: 'event', label: 'Event', icon: '/assets/images/staticemo/admission-tickets_1f39f-fe0f.png', action: 'openCreateEvent' },
    { id: 'group', label: 'Group', icon: '/assets/images/staticemo/performing-arts_1f3ad.png', action: 'openCreateGroup' },
  ];

  const handleAddMenuAction = (action: string) => {
    setShowAddDropdown(false);
    
    // Haptic feedback
    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }

    // Dispatch appropriate event or navigate
    switch (action) {
      case 'openCreatePost':
        const postEvent = new CustomEvent('openCreatePost');
        document.dispatchEvent(postEvent);
        console.log('[Header] 📝 Open create post');
        break;
      case 'openSpeed':
        setShowSpeedCreationSheet(true);
        console.log('[Header] ⚡ Open speed dating sheet');
        break;
      case 'openTravel':
        const travelEvent = new CustomEvent('openTravel');
        document.dispatchEvent(travelEvent);
        console.log('[Header] ✈️ Open travel');
        break;
      case 'openCreateEvent':
        console.log('[Header] 🎫 Open create event');
        // Check if we're already on the events page
        if (location === '/events' || location.startsWith('/events?')) {
          // Already on events page, dispatch event to open creation modal
          const createEvent = new CustomEvent('openCreateEventModal');
          document.dispatchEvent(createEvent);
          console.log('[Header] 🎫 Dispatched openCreateEventModal event');
        } else {
          // Navigate to events page
          setLocation('/events');
          // Dispatch event after navigation
          setTimeout(() => {
            const createEvent = new CustomEvent('openCreateEventModal');
            document.dispatchEvent(createEvent);
          }, 100);
        }
        break;
      case 'openCreateGroup':
        console.log('[Header] 🎭 Open create group');
        // Check if we're already on the groups page
        if (location === '/groups' || location.startsWith('/groups?')) {
          // Already on groups page, dispatch event to open creation modal
          const createGroupEvent = new CustomEvent('openCreateGroupModal');
          document.dispatchEvent(createGroupEvent);
          console.log('[Header] 🎭 Dispatched openCreateGroupModal event');
        } else {
          // Navigate to groups page with a query flag so the page opens the form on mount
          setLocation('/groups?action=create');
        }
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.add-dropdown-container')) {
        setShowAddDropdown(false);
      }
    };

    if (showAddDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showAddDropdown]);

  const handleAvatarClick = useCallback(() => {
    // Trigger feedback tattile quando si clicca sull'avatar
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.selectionChanged(); // O 'light' per un impatto più sottile
        console.log('[Header] 👤 Feedback tattile - avatar cliccato');
      } else if (navigator.vibrate) {
        navigator.vibrate(30); // Vibrazione per dispositivi non-Telegram
        console.log('[Header] 📳 Fallback vibrazione - avatar cliccato');
      }
    } catch (error) {
      console.error('[Header] ❌ Errore feedback tattile avatar:', error);
    }

    // Apri la scheda delle notifiche di partecipazione
    setExpandedEmoji(0); // Imposta l'emoji "📣" come attiva
  }, []);

  const handleAdminNavigation = useCallback(() => {
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
      }
    } catch (error) {
      console.error('[Header] ❌ Errore feedback tattile admin:', error);
    }
    setLocation('/admin');
  }, [setLocation]);

  const handleOrganizerNavigation = useCallback(() => {
    try {
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
      }
    } catch (_) {}
    setLocation('/organizer');
  }, [setLocation]);

  // Get canEditCurrentEvent from context
  const { canEditCurrentEvent } = useApp();

  // State per gestire l'edit mode del profilo
  const { profileEditMode, setProfileEditMode } = useApp();

  // DEBUG: Log user.photos per capire perché l'avatar non mostra la foto
  useEffect(() => {
    if (user) {
      console.log('[Header] 👤 User avatar debug:', { 
        username: user.username, 
        userId: user.id, 
        photos: user.photos,
        photosLength: user.photos?.length,
        hasPhotos: Array.isArray(user.photos) && user.photos.length > 0
      });
    }
  }, [user]);

  return (
    <div
      className={`fixed top-0 left-0 right-0 max-w-md lg:max-w-full mx-auto lg:mx-0 lg:left-60 flex flex-col tg-bg`}
      style={{
        zIndex: isAnyProfileSheetOpen ? -1 : 50,
        opacity: isAnyProfileSheetOpen ? 0 : 1,
        pointerEvents: isAnyProfileSheetOpen ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
        // Safe-area: in PWA standalone (viewport-fit=cover) la status bar/notch
        // si sovrappone; spingiamo il contenuto sotto l'inset. env() = 0 in
        // Telegram/browser normale, quindi è innocuo lì.
        height: `calc(${hideSecondRow ? 'var(--kg-header-row1)' : 'calc(var(--kg-header-row1) + 56px)'} + var(--safe-top))`,
        paddingTop: 'var(--safe-top)',
      }}
    >
      {/* RIGA 1 - altezza via --kg-header-row1 (48px browser/PWA, 90px Telegram) - emoji menu + admin button */}
      <div
        className="flex-shrink-0 flex justify-center items-end relative w-full px-4 pb-0 mb-0"
        style={{
          height: 'var(--kg-header-row1)',
          zIndex: isAnyProfileSheetOpen ? 99999 : 'auto',
          position: 'relative',
          paddingBottom: '5px',
          marginBottom: 0
        }}
      >
        {showBackButton && (
          <button
            className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white p-1 rounded-full"
            onClick={handleBackClick}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}

        {/* Edit buttons rimossi - ora sono dentro i rispettivi sheet (event-detail-sheet e user-profile-bottom-sheet) */}

        {customTitle && customTitle === "Kinkygram" ? (
          <div className="flex items-center justify-center gap-2 pb-1">
            <img
              src="https://em-content.zobj.net/source/telegram/386/hamster_1f439.webp"
              alt="Hamster"
              className="w-8 h-8 cursor-pointer transition-all duration-300 hover:scale-110 select-none"
              onClick={(event) => {
                // Trigger native Telegram emoji effect if available
                try {
                  if (window.Telegram?.WebApp?.HapticFeedback) {
                    window.Telegram.WebApp.HapticFeedback.impactOccurred('medium');
                    console.log('[Header] 🐹 Feedback tattile - click hamster');
                  } else if (navigator.vibrate) {
                    navigator.vibrate(60);
                    console.log('[Header] 📳 Fallback vibrazione - click hamster');
                  }
                } catch (error) {
                  console.error('[Header] ❌ Errore feedback tattile hamster:', error);
                }

                // Add our custom animation
                const element = event.target as HTMLElement;
                if (element) {
                  element.classList.add('emoji-active');
                  setTimeout(() => {
                    element.classList.remove('emoji-active');
                  }, 1200);
                }
              }}
              style={{
                userSelect: 'none',
                WebkitUserSelect: 'none',
                filter: 'drop-shadow(0 0 2px rgba(255, 193, 7, 0.3))'
              }}
            />
            <h1 className="text-white text-xl font-semibold">{customTitle}</h1>
          </div>
        ) : customTitle ? (
          <div className="flex items-center justify-center pb-1">
            <h1 className="text-white text-xl font-semibold">{customTitle}</h1>
          </div>
        ) : (
          // Show logo only when no sheets are open
          showLogo && !isProfileOpen && !finalIsEventSheetOpen && !isAnyProfileSheetOpen && !expandedVersion && expandedEmoji === null && (
            <div className="flex items-center justify-center transition-opacity duration-300 opacity-100 mx-auto">
              {/* Single unified feed notification - UNIFIED COMPONENT */}
              {/* Hidden when any sheet is open */}
              {!expandedVersion && expandedEmoji === null && (
              <div
                className="relative h-[30px] flex items-center justify-center cursor-pointer rounded-full transition-all duration-300"
                onClick={() => handleEmojiClick(0)}
                style={{
                  minWidth: '32px',
                  backgroundColor: (totalNotificationsCount > 0 || expandedEmoji !== null) ? undefined : '#252525'
                }}
              >
                {/* Background gradient - only visible when has notifications OR expanded */}
                {(totalNotificationsCount > 0 || expandedEmoji !== null) && (
                  <div
                    className="absolute w-full h-full rounded-full transition-all duration-300"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      background: 'linear-gradient(to right, #4fb3d4, #2da5ff, #1e90ff)',
                      padding: '2px'
                    }}
                  >
                    <div
                      className="w-full h-full rounded-full"
                      style={{
                        backgroundColor: 'var(--tg-bg-color)'
                      }}
                    />
                  </div>
                )}

                {/* Border - visible when has notifications OR expanded */}
                {(totalNotificationsCount > 0 || expandedEmoji !== null) && (
                  <div
                    className="absolute rounded-full border-2"
                    style={{
                      inset: '2px',
                      zIndex: 19,
                      backgroundColor: '#303030',
                      borderColor: 'var(--tg-bg-color)',
                      animation: expandedEmoji !== null ? 'borderExpandIn 0.3s ease-out forwards' : undefined,
                      transformOrigin: 'center center'
                    }}
                  />
                )}

                {/* Content with icon and text */}
                <div
                  className="relative flex items-center gap-1.5 px-2"
                  style={{
                    zIndex: 20
                  }}
                >
                  {/* Trumpet icon - animated when has notifications OR expanded */}
                  <img
                    src={(totalNotificationsCount > 0 || expandedEmoji !== null) ? feedNotification.animatedUrl : feedNotification.staticUrl}
                    alt={feedNotification.alt}
                    className={`w-4 h-4 object-contain ${(totalNotificationsCount > 0 || expandedEmoji !== null) ? 'animate-megaphone-pulse' : ''}`}
                  />

                  {/* Text: Count when has notifications, "Feeds" when expanded or no notifications */}
                  <span className="text-white text-xs font-medium">
                    {totalNotificationsCount > 0 && expandedEmoji === null
                      ? `${totalNotificationsCount} ${totalNotificationsCount === 1 ? 'Feed' : 'Feeds'}`
                      : 'Feeds'}
                  </span>
                </div>
              </div>
              )}

              {/* Version Button - Visible to all users */}
              {/* Hidden when any sheet is open */}
              {!expandedVersion && expandedEmoji === null && (
              <div
                className="relative h-[24px] flex items-center justify-center cursor-pointer rounded-full transition-all duration-300 ml-2"
                onClick={() => {
                  if (window.Telegram?.WebApp?.HapticFeedback) {
                    window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
                  }
                  // Toggle version section, close feed if open
                  if (expandedVersion) {
                    setExpandedVersion(false);
                  } else {
                    setExpandedVersion(true);
                    setExpandedEmoji(null); // Close feed if open
                    // Mark new version as seen
                    if (hasNewVersion && !hasSeenNewVersion) {
                      setHasSeenNewVersion(true);
                      localStorage.setItem('lastSeenVersion', latestVersion);
                    }
                  }
                }}
                style={{
                  minWidth: '28px',
                  backgroundColor: (hasNewVersion || expandedVersion) ? undefined : '#252525'
                }}
              >
                {/* Background gradient - visible when has new version (not seen) OR expanded */}
                {((hasNewVersion && !hasSeenNewVersion) || expandedVersion) && (
                  <div
                    className="absolute w-full h-full rounded-full transition-all duration-300"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 10,
                      background: 'linear-gradient(to right, #4fb3d4, #2da5ff, #1e90ff)',
                      padding: '2px'
                    }}
                  >
                    <div
                      className="w-full h-full rounded-full"
                      style={{
                        backgroundColor: 'var(--tg-bg-color)'
                      }}
                    />
                  </div>
                )}

                {/* Border - visible when has new version (not seen) OR expanded */}
                {((hasNewVersion && !hasSeenNewVersion) || expandedVersion) && (
                  <div
                    className="absolute rounded-full border-2"
                    style={{
                      inset: '2px',
                      zIndex: 19,
                      backgroundColor: '#303030',
                      borderColor: 'var(--tg-bg-color)',
                      animation: expandedVersion ? 'borderExpandIn 0.3s ease-out forwards' : undefined,
                      transformOrigin: 'center center'
                    }}
                  />
                )}

                {/* Content with circular image and text */}
                <div
                  className="relative flex items-center gap-1 px-1.5"
                  style={{
                    zIndex: 20
                  }}
                >
                  {/* Circular app icon - smaller */}
                  <img
                    src="/assets/images/kinkytele@0.5x.webp"
                    alt="KinkyTele"
                    className="w-3 h-3 rounded-full object-cover"
                  />
                  {/* Version text - smaller */}
                  <span className="text-white text-[10px] font-medium">
                    {hasNewVersion ? latestVersion : APP_VERSION}
                  </span>
                </div>
              </div>
              )}

            </div>
          )
        )}
      </div>

      {/* RIGA 2: 56px - Avatar + City Selector + Search */}
      {!hideSecondRow && (
      <div className="h-[56px] flex-shrink-0 flex items-center px-4 gap-3 relative pb-0 mb-0" style={{ paddingBottom: 0, marginBottom: 0 }}>
        {/* Avatar clickabile - 36x36px */}
        {user && (
          <button
            className="flex-shrink-0 cursor-pointer p-0 border-0 bg-transparent"
            onClick={() => {
              console.log('[HEADER] 🔥 Avatar clicked, dispatching openUserProfile event for userId:', user.id);
              if (window.Telegram?.WebApp?.HapticFeedback) {
                window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
              }
              const event = new CustomEvent('openUserProfile', {
                detail: { userId: user.id }
              });
              document.dispatchEvent(event);
              console.log('[HEADER] ✅ Event dispatched');
            }}
            style={{ padding: 0 }}
          >
            <div className="w-8 h-8 rounded-full overflow-hidden relative" style={{ width: '32px', height: '32px' }}>
              <TelegramImage
                photoUrl={(() => {
                  // Usa prima userPhotos dalla query dedicata
                  if (userPhotos?.photos && Array.isArray(userPhotos.photos) && userPhotos.photos.length > 0) {
                    const firstPhoto = userPhotos.photos[0] as { url?: string } | string | undefined;
                    if (firstPhoto && typeof firstPhoto === 'object' && 'url' in firstPhoto) {
                      return decodeURIComponent((firstPhoto as { url: string }).url);
                    }
                    if (typeof firstPhoto === 'string') {
                      return decodeURIComponent(firstPhoto);
                    }
                  }
                  // Fallback: usa user.photos direttamente
                  if (user.photos && Array.isArray(user.photos) && user.photos.length > 0) {
                    const firstPhoto = user.photos[0] as { url?: string } | string | undefined;
                    if (firstPhoto && typeof firstPhoto === 'object' && 'url' in firstPhoto) {
                      return decodeURIComponent((firstPhoto as { url: string }).url);
                    }
                    if (typeof firstPhoto === 'string') {
                      return decodeURIComponent(firstPhoto);
                    }
                  }
                  return undefined;
                })()}
                userId={user.id}
                username={user.username}
                variant="avatar"
                className="w-full h-full"
                style={{ width: '100%', height: '100%' }}
              />
            </div>
          </button>
        )}

        {/* City Selector */}
        <div className="flex-1 self-center">
          <SimpleCitySelector 
            className="h-[36px]"
            defaultCity={user?.city as string || ''}
          />
        </div>

        {/* Filter Button - FilterVerticalIcon da Hugeicons */}
        <button
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center"
          onClick={() => {
            if (window.Telegram?.WebApp?.HapticFeedback) {
              window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
            }
            setShowFilterSheet(true);
            console.log('[Header] 🔍 Filter button clicked');
          }}
        >
          <HugeiconsIcon
            icon={FilterVerticalIcon}
            className="w-6 h-6 tg-text"
            size={24}
          />
        </button>

        {/* Search Button - solo icona senza contenitore */}
        <button
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center"
          onClick={() => {
            if (window.Telegram?.WebApp?.HapticFeedback) {
              window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
            }
            setShowSearchSheet(true);
            console.log('[Header] 🔍 Search button clicked');
          }}
        >
          <Search className="w-6 h-6 tg-text" />
        </button>

        {/* Add Button with Dropdown - AddSquareIcon da Hugeicons */}
        <div className="relative add-dropdown-container">
          <button
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center"
            onClick={() => {
              if (window.Telegram?.WebApp?.HapticFeedback) {
                window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
              }
              setShowAddDropdown(!showAddDropdown);
              console.log('[Header] ➕ Add button clicked, dropdown:', !showAddDropdown);
            }}
          >
            <HugeiconsIcon 
              icon={AddSquareIcon} 
              className="w-6 h-6 tg-text" 
              size={24}
            />
          </button>

          {/* Dropdown Menu - si apre verso il basso */}
          {showAddDropdown && (
            <div 
              className="absolute top-full right-0 mt-2 w-36 rounded-xl shadow-xl py-2 z-50"
              style={{ 
                backgroundColor: 'rgba(30, 30, 30, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
            >
              {addMenuItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleAddMenuAction(item.action)}
                  className="w-full px-3 py-2 flex items-center gap-2 text-left text-sm text-white hover:bg-white/10 transition-colors"
                >
                  {item.icon.startsWith('/') ? (
                    <img 
                      src={item.icon} 
                      alt={item.label} 
                      className="w-5 h-5 flex-shrink-0"
                    />
                  ) : (
                    <span className="w-5 h-5 flex-shrink-0 text-base">{item.icon}</span>
                  )}
                  <span className="flex-shrink-0">{item.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Organizer Button - Visible to group owners/co-owners (non-admin) */}
        {isOrganizer && !user?.isAdmin && (
          <button
            onClick={handleOrganizerNavigation}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center"
            title="Pannello Organizzatore"
          >
            <ClipboardList className="w-6 h-6 tg-text" />
          </button>
        )}

        {/* Creator Button - Visible to creators (userbot panel) */}
        {user?.isCreator && (
          <button
            onClick={() => setLocation('/creator-panel')}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center"
            title="Il tuo Bot"
          >
            <Bot className="w-6 h-6 tg-text" />
          </button>
        )}

        {/* Admin Button - Visible only to admins */}
        {user?.isAdmin && (
          <button
            onClick={handleAdminNavigation}
            className="flex-shrink-0 w-6 h-6 flex items-center justify-center relative"
            title="Admin Panel"
          >
            <HugeiconsIcon
              icon={Settings01Icon}
              className="w-6 h-6 tg-text"
              size={24}
            />
            {/* Badge per pending applications */}
            {pendingApplicationsCount > 0 && (
              <div
                className="absolute bg-red-600 rounded-full"
                style={{
                  top: '-2px',
                  right: '-2px',
                  width: '8px',
                  height: '8px',
                  zIndex: 50
                }}
              />
            )}
          </button>
        )}
      </div>
      )}

      {/* Notifications Sheet - Separate from version */}
      {expandedEmoji !== null && (
        <div className="fixed inset-0 z-[1] px-4 pt-[calc(var(--kg-header-row1)_+_var(--safe-top))] pb-20 animate-in slide-in-from-bottom-5 duration-300 min-h-screen" style={{ backgroundColor: '#0c0c0c' }}>
          <div className="text-white text-center w-full h-full min-h-screen">
            <UnifiedNotificationsSection setExpandedEmoji={setExpandedEmoji} />
          </div>
        </div>
      )}

      {/* Version Sheet - Separate from notifications */}
      {expandedVersion && (
        <div className="fixed inset-0 z-[1] px-4 pt-[calc(var(--kg-header-row1)_+_var(--safe-top))] pb-32 animate-in slide-in-from-bottom-5 duration-300" style={{ backgroundColor: '#0c0c0c', overflowY: 'auto' }}>
          <div className="text-white text-center w-full pb-20">
            <VersionSection setExpandedVersion={setExpandedVersion} appVersion={APP_VERSION} />
          </div>
        </div>
      )}


      {/* User Search Sheet */}
      {showSearchSheet && (
        <UserSearchSheet
          isOpen={showSearchSheet}
          onClose={() => setShowSearchSheet(false)}
          onUserSelected={(userId) => {
            // Open user profile when selected
            if (window.Telegram?.WebApp?.HapticFeedback) {
              window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
            }
            const event = new CustomEvent('openUserProfile', {
              detail: { userId }
            });
            document.dispatchEvent(event);
          }}
        />
      )}

      {/* Filter Sheet */}
      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        selectedFilters={selectedFilters}
        onFiltersChange={handleFiltersChange}
      />

      {/* Speed Date Creation Sheet */}
      <SpeedDateCreationSheet
        isOpen={showSpeedCreationSheet}
        onClose={() => setShowSpeedCreationSheet(false)}
      />
    </div>
  );
}
